import { NamedBitmapFilter } from 'cio/utils/filters';
import { SerializedLegacyMessageFilter } from 'cio/types/conditions/legacy-filter';
import { TYPE_MAP } from 'cio/utils/named-bitmap-filters';

// This matches event names of the format:
// se-email-campaign_10
// sa-webhook-campaign_5
// which is used for matching any message action of a given type (email, webhook
// etc) in a specific campaign.
// The capture groups are:
// - first, the message type (se, sa etc, according to TYPE_MAP)
// - second, the action type
// - third, the campaign ID
const anyInParentRegex = /([^-]+)-(.+)-campaign_(.+)/;

// The name key in the filter, represents the encoded base64 string of the message type
// - Examples: c2Etc2xhY2s= -> sa-slack, c2U= -> sa
export default function namedBitmapToCondition(
  { named_bitmap: filter }: NamedBitmapFilter,
  not = false,
): SerializedLegacyMessageFilter {
  let { name } = filter;
  let messageType = atob(name);
  let messageName;

  // if there is a dash in the name, the event refers to a specific action type
  // or ID
  if (messageType.includes('-')) {
    let anyInParentMatch = messageType.match(anyInParentRegex);

    if (anyInParentMatch != null) {
      let [, eventType, actionType, campaignId] = anyInParentMatch;
      messageType = eventType;
      messageName = `${actionType}-campaign_${campaignId}`;
    } else {
      // handle cases where the message name also contains `-` characters
      let [type, ...rest] = messageType.split('-');

      messageType = type;
      messageName = rest.join('-');
      // if there is no underscore in the message name (id), then the filter refers
      // to all messages of that type. If it's a non-email action, we'll need to add
      // an asterisk (*) to map correctly to the UI filters format.
      // Note: in_app includes an '_' so it requires a separate check to determine if it's all in-app or a specific in-app
      if (
        (!messageName.includes('_') || (messageName.includes('in_app') && !messageName.includes('in_app_'))) &&
        !['output', 'newsletter'].includes(messageName) &&
        messageType !== TYPE_MAP.event
      ) {
        messageName = `${messageName}_*`;
      }
    }
  } else {
    // if there is no dash, then the event refers to all EMAIL actions. We need
    // to use an asterisk for the messageName in order for the UI to work
    // properly
    messageName = '*';
  }

  // now we need to map the message type from what the backend gives us to a
  // format recognized by the UI
  let [uiEventType] = Object.entries(TYPE_MAP).find(([_, value]) => value === messageType)!;

  return {
    event: {
      type: uiEventType,
      name: messageName,
      filters: {},
    },
    times: 1,
    within: 0,
    inverse: not,
    type: 'message',
  };
}
